import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useCampaignStore, useCoachmarkStore, useFieldOpsStore } from '@/stores';
import { useMayManageCampaign } from '@/hooks';
import { useVoters } from '@/hooks/queries';
import { Coachmark } from '@/components/onboarding';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from '@/components/ui';
import { TurfDrawDemoOverlay } from './TurfDrawDemoOverlay';

// 'heads-up' precedes 'cut-button' only when the campaign has no voter data — turf is drawn over
// voters, so without a file they can preview the walkthrough but can't actually save a turf.
type Step = 'idle' | 'heads-up' | 'cut-button' | 'draw-demo' | 'filters' | 'done';

// Guided sub-tour after a shape is saved: spotlight the Layers button, open it, then walk the
// filters that help a manager decide where to cut. Steps 1+ require the Layers dropdown open.
const FILTER_TOUR: { selector: string; message: string; label: string }[] = [
  {
    selector: '[data-coachmark="map-layers"]',
    message: 'Nice — that’s a turf. Now pick smart places to cut: these map layers help you target the right areas. Open them.',
    label: 'Show me',
  },
  {
    selector: '[data-coachmark="layer-demographics"]',
    message: 'Census Demographics — target by income, education, or community to find persuadable neighborhoods.',
    label: 'Next',
  },
  {
    selector: '[data-coachmark="layer-contact-rate"]',
    message: 'Turf Contact Rate — see where you’ve already knocked so you don’t double-cover.',
    label: 'Next',
  },
  {
    selector: '[data-coachmark="layer-election"]',
    message: '2024 Election Results — spot friendly vs. swing precincts to prioritize.',
    label: 'Done',
  },
];

/**
 * One-time guided walkthrough for cutting a turf. Rendered at the Field Ops PAGE level (not inside
 * FieldOpsView, which swaps its whole subtree per mode) so it stays mounted across
 * management → cutting → management. Reads the draw lifecycle from the field-ops store.
 *
 *   S1 spotlight the Cut Turf control → (enter cutting)
 *   S2 animated "how to draw" demo    → (they save a turf)
 *   S3 guided tour of the map filters → done (persist a per-campaign "seen" flag)
 */
export function TurfCutWalkthrough() {
  const campaignId = useCampaignStore((s) => s.selectedCampaign?.id);
  const mode = useFieldOpsStore((s) => s.mode);
  const lastCreatedTurfId = useFieldOpsStore((s) => s.lastCreatedTurfId);
  const setLayersPanelOpen = useFieldOpsStore((s) => s.setLayersPanelOpen);
  const setTurfListExpanded = useFieldOpsStore((s) => s.setTurfListExpanded);
  const coachmarkTarget = useCoachmarkStore((s) => s.target);
  const clearCoachmark = useCoachmarkStore((s) => s.clear);
  const requestCoachmark = useCoachmarkStore((s) => s.request);
  const navigate = useNavigate();
  const mayImport = useMayManageCampaign();
  // Cheap count: does this campaign have any voter data? Drives the heads-up before the walkthrough.
  const voterListRead = useVoters({ limit: 1 });
  const voterList = voterListRead.status === 'ready' ? voterListRead.data : undefined;

  const seenKey = `sv:turf-demo-seen:${campaignId ?? 'none'}`;
  // Set when the manager leaves the heads-up to go fetch voter data, so returning to Field Ops
  // resumes straight into the cut-turf demo — independent of the (possibly stale-cached or
  // still-importing) voter count.
  const resumeKey = `sv:turf-resume:${campaignId ?? 'none'}`;
  const [step, setStep] = useState<Step>('idle');
  const [demoVisible, setDemoVisible] = useState(true);
  const [filterIndex, setFilterIndex] = useState(0);
  // "Got it" on the S1 spotlight hides the hint but keeps the walkthrough armed, so clicking
  // Cut Turf afterwards still plays the draw demo.
  const [s1Dismissed, setS1Dismissed] = useState(false);
  const startedRef = useRef(false);
  // Snapshot of the latest-created turf when entering cutting, to detect a fresh save on exit.
  const turfSnapshotRef = useRef<string | null>(null);

  // Mark this campaign's turf demo as seen so it shows at most once — regardless of how the
  // manager exits (completes, skips, or just navigates away mid-flow). Persisted at start.
  const markSeen = useCallback(() => {
    try {
      localStorage.setItem(seenKey, '1');
    } catch {
      /* storage disabled — it may just show again, which is fine */
    }
  }, [seenKey]);

  // Un-mark the demo so it re-arms next time they land on Field Ops. Used when they leave the
  // heads-up to go fetch voter data: returning *with* data should play the real cut-turf
  // walkthrough — otherwise the "seen at start" flag would suppress it forever.
  const clearSeen = useCallback(() => {
    try {
      localStorage.removeItem(seenKey);
    } catch {
      /* ignore */
    }
  }, [seenKey]);

  const finish = useCallback(() => {
    setStep('done');
    markSeen();
  }, [markSeen]);

  // Start once per campaign: first time here (not seen) or via the onboarding turf hand-off.
  useEffect(() => {
    if (startedRef.current || step !== 'idle' || !campaignId) return;
    const forced = coachmarkTarget === 'draw-turf';
    let seen = false;
    try {
      seen = localStorage.getItem(seenKey) === '1';
    } catch {
      /* ignore */
    }
    // Resuming after a 'Get voter data' detour → go straight to the cut-turf demo, regardless of
    // the voter count (which may be a stale cache or a still-processing import).
    let resuming = false;
    try {
      resuming = localStorage.getItem(resumeKey) === '1';
    } catch {
      /* ignore */
    }
    if (resuming && mode === 'management') {
      try {
        localStorage.removeItem(resumeKey);
      } catch {
        /* ignore */
      }
      startedRef.current = true;
      markSeen();
      setStep('cut-button');
      return;
    }

    if ((forced || !seen) && mode === 'management') {
      // Wait for the voter count before starting so we can decide whether to lead with the
      // "you'll need voter data" heads-up.
      if (voterList === undefined) return;
      if (forced) clearCoachmark();
      startedRef.current = true;
      markSeen(); // once it starts, don't show it again for this campaign
      setStep(voterList.total === 0 ? 'heads-up' : 'cut-button');
    }
  }, [campaignId, coachmarkTarget, clearCoachmark, markSeen, seenKey, resumeKey, mode, step, voterList]);

  // The cut-turf spotlight points at the "Cut New Turf" button, which only renders when the Field
  // Ops panel is expanded. Force it open whenever we reach that step so the spotlight always has a
  // laid-out target — the collapsed panel shows only a tiny pill that doesn't anchor it reliably
  // (e.g. when resuming from a 'Get voter data' detour, the panel comes back collapsed).
  useEffect(() => {
    if (step === 'cut-button') setTurfListExpanded(true);
  }, [step, setTurfListExpanded]);

  // S1 → S2: they clicked Cut Turf and entered drawing mode. Snapshot the latest turf id.
  useEffect(() => {
    if (step === 'cut-button' && mode === 'cutting') {
      turfSnapshotRef.current = lastCreatedTurfId;
      setDemoVisible(true);
      setStep('draw-demo');
    }
  }, [step, mode, lastCreatedTurfId]);

  // Leaving cutting: if a new turf was saved, run the filters tour; if they cancelled, end.
  useEffect(() => {
    if (step === 'draw-demo' && mode === 'management') {
      const savedNew = !!lastCreatedTurfId && lastCreatedTurfId !== turfSnapshotRef.current;
      if (savedNew) {
        setFilterIndex(0);
        setStep('filters');
      } else {
        finish();
      }
    }
  }, [step, mode, lastCreatedTurfId, finish]);

  // Keep the Layers dropdown open through the filter steps that live inside it (1+).
  useEffect(() => {
    if (step === 'filters' && filterIndex >= 1) setLayersPanelOpen(true);
  }, [step, filterIndex, setLayersPanelOpen]);

  // Heads-up before the walkthrough when the campaign has no voter data: they can preview how
  // turf-cutting works, but can't save a real turf until a voter file is in. Offers a path to get it.
  if (step === 'heads-up') {
    return (
      <Dialog open onOpenChange={(open) => { if (!open) finish(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>You&rsquo;ll need voter data to cut turf</DialogTitle>
            <DialogDescription>
              Turf is drawn over your voters, so you&rsquo;ll need a voter file before you can save a
              real turf. You can still preview how turf-cutting works.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            {mayImport && (
              <Button
                variant="outline"
                onClick={() => {
                  requestCoachmark('voter-import');
                  // Re-arm + flag a resume so returning plays the real cut-turf demo (independent of
                  // the voter count, which may be stale-cached or still importing).
                  clearSeen();
                  try {
                    localStorage.setItem(resumeKey, '1');
                  } catch {
                    /* ignore */
                  }
                  setStep('done');
                  navigate({ to: '/app/settings/campaign-preferences' });
                }}
              >
                Get voter data
              </Button>
            )}
            <Button onClick={() => setStep('cut-button')}>Preview the walkthrough</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // S1 hint. Dismissing it ("Got it") only hides the hint — the step stays 'cut-button', so the
  // draw demo still triggers when they click Cut Turf next (handled by the step→cutting effect).
  if (step === 'cut-button' && !s1Dismissed) {
    return (
      <Coachmark
        active
        targetSelector='[data-coachmark="draw-turf"]'
        message="Start by cutting a turf — the area you'll send canvassers to. Click here to begin."
        onDismiss={() => setS1Dismissed(true)}
      />
    );
  }

  if (step === 'draw-demo' && demoVisible) {
    return <TurfDrawDemoOverlay onDismiss={() => setDemoVisible(false)} />;
  }

  if (step === 'filters') {
    const tour = FILTER_TOUR[filterIndex];
    if (!tour) return null;
    const isLast = filterIndex === FILTER_TOUR.length - 1;
    return (
      <Coachmark
        active
        targetSelector={tour.selector}
        message={tour.message}
        primaryLabel={tour.label}
        onDismiss={() => {
          if (isLast) {
            setLayersPanelOpen(false);
            finish();
            return;
          }
          if (filterIndex === 0) setLayersPanelOpen(true);
          setFilterIndex((i) => i + 1);
        }}
      />
    );
  }

  return null;
}
